import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useRef, useState } from 'react';
import {
  twoStepConfirmMethod,
  twoStepSendCode,
  twoStepStartAuthenticator,
  twoStepStartSms,
  twoStepStatus,
  twoStepVerify,
  twoStepVerifyDevice,
  type ApiError,
  type TwoStepMethodKind,
  type TwoStepSignIn,
  type TwoStepStatus,
} from '../../../../../@api-client';
import { zTwoStepMethodKind } from '../../../../../@api-client/zod.gen';
import { refusalCodeFromError } from '../../../../utils/api/providers/api-error-status';
import { deviceLabelFor } from './device-label';
import type {
  TwoStepActions,
  TwoStepError,
  TwoStepFeedback,
  TwoStepStep,
  TwoStepUsing,
  TwoStepVerifyStep,
} from './two-step.types';

export const TWO_STEP_GENERIC_ERROR = "Two-step sign-in didn't work. Try again.";
export const TWO_STEP_PASSED_BUT_REFUSED =
  "Your sign-in passed two-step sign-in, but it wasn't accepted. Try again, or contact iDU support.";
export const TWO_STEP_DEVICE_KEY_PREFIX = 'twoStepDevice:';
export const TWO_STEP_RESENT_NOTICE = 'Sent again. It can take a minute to arrive.';

export type TwoStepSession = {
  token: string;
  uid?: string;
  email?: string;
  googleIdToken?: string;
};

export type TwoStepIntent = 'sign-in' | 'verify' | 'add-method' | 'change-sms';

/** Only a refusal carrying `error.cause.code` is written for people; anything else gets the generic message. */
export const toTwoStepError = (failure: unknown): TwoStepError => {
  const message = (failure as Partial<ApiError> | undefined)?.error?.message;
  const code = refusalCodeFromError(failure);
  return code && message ? { code, message } : { message: TWO_STEP_GENERIC_ERROR };
};

const deviceKey = (uid: string) => `${TWO_STEP_DEVICE_KEY_PREFIX}${uid}`;

const usableMethods = (status: TwoStepStatus) =>
  status.methods.filter((method) => method.kind !== 'sms' || status.smsAvailable);

export const twoStepKindsToSetUp = (status: TwoStepStatus): TwoStepMethodKind[] =>
  zTwoStepMethodKind.options.filter(
    (kind) =>
      (kind !== 'sms' || status.smsAvailable) &&
      !status.methods.some((method) => method.kind === kind),
  );

const verifyStepFor = (
  status: TwoStepStatus,
  intent: TwoStepIntent,
  email?: string,
): TwoStepVerifyStep => {
  const methods = usableMethods(status);
  const preferred = methods.find((method) => method.preferred) ?? methods[0] ?? status.methods[0];
  return {
    name: 'verify',
    email,
    methods,
    rememberDays: intent === 'sign-in' ? status.rememberDays : undefined,
    // A support code's pass can't authorise a change, so it is offered only to sign in.
    offersSupportCode: intent === 'sign-in',
    using: preferred.kind,
  };
};

/**
 * The second step of a sign-in, without any screen: reads the account's two-step status, passes
 * silently on a remembered device or a Google 2-Step Verification sign-in, and otherwise drives the
 * setup and verify steps rendered by `TwoStepSteps`.
 *
 * A pass signs in again with the issued custom token, so the session's next ID token carries
 * `second_factor`. The step then stays on screen, busy, until the caller moves on or calls `reset`;
 * `onPassed` is the hook for callers (Account Security) that retry a refused change after a pass,
 * and `onRefused` for callers that answer a step's refusal themselves.
 */
export const useTwoStepSignIn = ({
  onPassed,
  onRefused,
}: { onPassed?: () => void; onRefused?: (refusal: TwoStepError) => void } = {}) => {
  const [step, setStep] = useState<TwoStepStep | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TwoStepError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const session = useRef<TwoStepSession | null>(null);
  const phase = useRef<'idle' | 'checking' | 'asking' | 'passed'>('idle');

  const auth = () => ({
    headers: { Authorization: `Bearer ${session.current?.token ?? ''}` },
    throwOnError: true as const,
  });

  const go = (next: TwoStepStep | null) => {
    setError(null);
    setNotice(null);
    setStep(next);
  };

  const run = async (action: () => Promise<void>) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
    } catch (failure) {
      const refusal = toTwoStepError(failure);
      setError(refusal);
      onRefused?.(refusal);
    } finally {
      setBusy(phase.current === 'passed');
    }
  };

  // Passed before the sign-in resolves: its new token can restart the check before this call returns.
  const signInWithPass = async (pass: TwoStepSignIn) => {
    const uid = session.current?.uid;
    const previous = phase.current;
    phase.current = 'passed';
    try {
      await FirebaseAuthentication.signInWithCustomToken({ token: pass.token });
    } catch (failure) {
      phase.current = previous;
      throw failure;
    }
    if (pass.deviceToken && uid) localStorage.setItem(deviceKey(uid), pass.deviceToken);
    onPassed?.();
  };

  const passOnRememberedDevice = async () => {
    const uid = session.current?.uid;
    const deviceToken = uid ? localStorage.getItem(deviceKey(uid)) : null;
    if (!uid || !deviceToken) return false;
    try {
      const { data } = await twoStepVerifyDevice({ ...auth(), body: { deviceToken } });
      await signInWithPass(data.data);
      return true;
    } catch (failure) {
      if (toTwoStepError(failure).code === 'TWO_STEP_DEVICE_NOT_REMEMBERED') {
        localStorage.removeItem(deviceKey(uid));
      }
      return false;
    }
  };

  const passWithGoogle = async (googleIdToken?: string) => {
    if (!googleIdToken) return false;
    try {
      const { data } = await twoStepVerify({ ...auth(), body: { googleIdToken } });
      await signInWithPass(data.data);
      return true;
    } catch {
      return false;
    }
  };

  const sendCode = async () => {
    await twoStepSendCode({ ...auth(), body: { kind: 'sms' } });
  };

  const openMethod = (current: TwoStepVerifyStep, using: TwoStepUsing) => {
    go({ ...current, using });
    if (using === 'sms') void run(sendCode);
  };

  const askFor = (status: TwoStepStatus, intent: TwoStepIntent, email?: string) => {
    phase.current = 'asking';
    if (intent === 'change-sms') {
      return go({ name: 'setup', email, kinds: ['sms'], askingPhone: true, changingNumber: true });
    }
    if (intent === 'add-method' || status.methods.length === 0) {
      const { phoneOnRecord } = status;
      return go({
        name: 'setup',
        email,
        kinds: twoStepKindsToSetUp(status),
        ...(phoneOnRecord && { phoneOnRecord }),
      });
    }
    const verify = verifyStepFor(status, intent, email);
    openMethod(verify, verify.using);
  };

  /**
   * Starts the second step for a signed-in session. Resolves `true` when the session may continue
   * without one (two-step is off, or this sign-in already passed), `false` while a step is asking.
   * Rejects with a person-facing message when the status cannot be read.
   */
  const start = async (next: TwoStepSession, intent: TwoStepIntent = 'sign-in') => {
    if (phase.current === 'checking' || phase.current === 'asking') {
      if (session.current) session.current = { ...session.current, token: next.token };
      return false;
    }

    const alreadyPassed = phase.current === 'passed';
    phase.current = 'checking';
    session.current = next;

    let status: TwoStepStatus;
    try {
      status = (await twoStepStatus(auth())).data.data;
    } catch (failure) {
      phase.current = 'idle';
      throw new Error(toTwoStepError(failure).message);
    }

    if (intent === 'sign-in' && (status.twoStep === 'off' || status.passed)) {
      phase.current = alreadyPassed ? 'passed' : 'idle';
      return true;
    }
    if (alreadyPassed && intent === 'sign-in') {
      phase.current = 'idle';
      throw new Error(TWO_STEP_PASSED_BUT_REFUSED);
    }

    const silentlyPassed =
      intent === 'sign-in' &&
      status.methods.length > 0 &&
      ((await passOnRememberedDevice()) || (await passWithGoogle(next.googleIdToken)));
    if (!silentlyPassed) askFor(status, intent, next.email);
    return false;
  };

  const reset = () => {
    phase.current = 'idle';
    session.current = null;
    setBusy(false);
    go(null);
  };

  const setupStep = step?.name === 'setup' ? step : null;
  const verifyStep = step?.name === 'verify' ? step : null;

  const actions: TwoStepActions = {
    chooseMethod: (kind) =>
      run(async () => {
        if (!setupStep) return;
        const { data } = await twoStepStartAuthenticator(auth());
        go({ ...setupStep, method: { kind, ...data.data } });
      }),
    askForPhone: () => setupStep && go({ ...setupStep, askingPhone: true }),
    submitPhone: (phoneNumber) =>
      run(async () => {
        if (!setupStep) return;
        const { data } = await twoStepStartSms({ ...auth(), body: { phoneNumber } });
        go({ ...setupStep, askingPhone: false, method: { kind: 'sms', hint: data.data.hint } });
      }),
    confirmMethod: (code) =>
      run(async () => {
        if (!setupStep?.method) return;
        const { data } = await twoStepConfirmMethod({
          ...auth(),
          path: { kind: setupStep.method.kind },
          body: { code },
        });
        await signInWithPass(data.data);
      }),
    chooseAnotherSetupMethod: () =>
      setupStep && go({ ...setupStep, askingPhone: undefined, method: undefined }),
    submitCode: (code, rememberDevice) =>
      run(async () => {
        if (!verifyStep) return;
        const verification =
          verifyStep.using === 'support-code'
            ? { supportCode: code }
            : { kind: verifyStep.using, code };
        const remember =
          rememberDevice && verifyStep.rememberDays !== undefined && 'kind' in verification
            ? { rememberDevice: true, deviceLabel: deviceLabelFor(navigator.userAgent) }
            : {};
        const { data } = await twoStepVerify({ ...auth(), body: { ...verification, ...remember } });
        await signInWithPass(data.data);
      }),
    switchMethod: (using) => verifyStep && openMethod(verifyStep, using),
    resendCode: () =>
      run(async () => {
        if (verifyStep?.using !== 'sms') return;
        await sendCode();
        setNotice(TWO_STEP_RESENT_NOTICE);
      }),
  };

  const feedback: TwoStepFeedback = { busy, error, notice };

  return { step, ...feedback, ...actions, start, reset };
};

export type TwoStepSignInState = ReturnType<typeof useTwoStepSignIn>;
